/// <amd-module name="Wiki/WikiPicker/WikiPicker" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { ILegacyPlatformService } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { IButtonProps } from "VSSUI/Button";
import { Dropdown, DropdownCallout, DropdownExpandableButton } from "VSSUI/Dropdown";
import { Icon } from "VSSUI/Icon";
import { IListSelection } from "VSSUI/List";
import { IListBoxItem, ListBoxItemType, LoadingCell } from "VSSUI/ListBox";
import { IMenuItem, MoreButton } from "VSSUI/Menu";
import { ITableColumn } from "VSSUI/Table";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { GroupedItemProvider } from "VSSUI/Utilities/GroupedItemProvider";
import { Location } from "VSSUI/Utilities/Position";

import * as VC_Constants from "Wiki/Clients/VC/Contracts/Constants";
import { WikiCreateParametersV2, WikiType, WikiV2 } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { getWikiClient } from "Wiki/Clients/Wiki/RestClient/Wiki";
import { ActionIds, RepoServiceContributionId, UrlConstants } from "Wiki/ViewCommon/Constants";
import { WikiInfo } from "Wiki/ViewCommon/Contracts";
import { TelemetryConstants } from "Wiki/ViewCommon/CustomerIntelligenceConstants";
import { getVersionFromBranchName, getWikiTypeString, isCodeWiki, navigateToUrl, publish } from "Wiki/ViewCommon/Utils/WikiHelper";
import { calculateGitSecuredToken } from "Wiki/ViewCommon/Utils/WikiPermissionsHelper";
import { LoadingSpinner, showWrappedDialog } from "Wiki/ViewCommon/WrappedDialogHelper";
import * as Resources from "Wiki/WikiPicker/Resources";

export interface IWikiPickerProps {
    wikiInfo: WikiInfo;
    showPublishCodeWikiAction: boolean;
    showCreateNewProjectWikiAction: boolean;
}

interface IWikiPickerState {
    showWikiSecurityLoadingSpinner?: boolean;
}

interface WikiGroupIdToName {
    groupId: string;
    name: string;
}

const wikiVersionPickerGroupKey = "published-branches";

const wikiGroupNames: WikiGroupIdToName[] = [
    { groupId: WikiType.ProjectWiki.toString(), name: Resources.ProjectWikiText },
    { groupId: WikiType.CodeWiki.toString(), name: Resources.CodeWikisText }
];

export class WikiPicker extends VssComponent<IWikiPickerProps, IWikiPickerState> {
    private _versionSelection: DropdownSelection;
    private _versionGroupedItemProvider: GroupedItemProvider<{}>;
    private _loadingWiki: ObservableValue<boolean>;
    private _wikiLoaderPromise: Promise<WikiV2[]>;

    private _wikiSelection: DropdownSelection;
    private _wikiGroupedItemProvider: GroupedItemProvider<{}>;

    private loadingItem: IListBoxItem = {
        id: "loading",
        type: ListBoxItemType.Loading,
        render: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IListBoxItem<{}>>, tableItem: IListBoxItem<{}>) => {
            return <LoadingCell key={rowIndex} columnIndex={columnIndex} tableColumn={tableColumn} tableItem={tableItem} />;
        }
    };

    constructor(props: IWikiPickerProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {};
        this._loadingWiki = new ObservableValue<boolean>(false);

        this._wikiSelection = new DropdownSelection();
        this._wikiGroupedItemProvider = new GroupedItemProvider<{}>([], [], true);

        this._wikiGroupedItemProvider.push(this.loadingItem);

        if (isCodeWiki(this.props.wikiInfo.wiki)) {
            this._versionSelection = new DropdownSelection();
            const versions: IListBoxItem<{}>[] = this.props.wikiInfo.wiki.versions.map(v => {
                return { id: v.version, text: v.version, iconProps: { iconName: "OpenSource" }, groupId: wikiVersionPickerGroupKey };
            });

            const groups = [{ id: wikiVersionPickerGroupKey, name: Resources.WikiVersionMenuItemsText }];
            this._versionGroupedItemProvider = new GroupedItemProvider(versions, groups, true);
            const versionSelectedIndex = this._versionGroupedItemProvider.value.findIndex(
                item => item.text === this.props.wikiInfo.wikiVersion.version
            );
            this._versionSelection.select(versionSelectedIndex);
        }
    }

    public render(): JSX.Element {
        return (
            <div className="wiki-picker-container flex-column">
                <div className="flex-row wiki-picker">
                    <div className="h-scroll-hidden flex-column">
                        <Dropdown
                            className="wiki-picker-button"
                            selection={this._wikiSelection}
                            items={this._wikiGroupedItemProvider}
                            loading={this._loadingWiki}
                            onExpand={this._onWikiPickerExpand}
                            actions={this._getWikiPickerActions()}
                            width={256}
                            onSelect={this._onWikiSelectionChange}
                            placeholder={this.props.wikiInfo.wiki.name}
                            renderExpandable={props => <DropdownExpandableButton {...props} />}
                            filterPlaceholderText={Resources.WikiPickerSearchTextPlaceholder}
                            renderCallout={props => (
                                <DropdownCallout
                                    {...props}
                                    dropdownOrigin={{
                                        horizontal: Location.start,
                                        vertical: Location.start
                                    }}
                                    anchorOrigin={{
                                        horizontal: Location.start,
                                        vertical: Location.end
                                    }}
                                />
                            )}
                        />
                    </div>
                    <MoreButton
                        contextualMenuProps={{
                            menuProps: {
                                id: "wikipicker-menu",
                                items: this._getMenuItems()
                            }
                        }}
                    />
                </div>
                {isCodeWiki(this.props.wikiInfo.wiki) && (
                    <>
                        <div className="flex-column" style={{ margin: "8px 0 0 0" }}>
                            <Dropdown
                                className="wiki-version-picker-button wiki-picker-button"
                                selection={this._versionSelection}
                                items={this._versionGroupedItemProvider}
                                actions={this._getWikiVersionPickerActions()}
                                onSelect={this._onWikiVersionSelectionChange}
                                renderExpandable={props => (
                                    <DropdownExpandableButton {...props} renderSelectedItems={this._onRenderSelectedVersion} />
                                )}
                                renderCallout={props => (
                                    <DropdownCallout
                                        {...props}
                                        dropdownOrigin={{
                                            horizontal: Location.start,
                                            vertical: Location.start
                                        }}
                                        anchorOrigin={{
                                            horizontal: Location.start,
                                            vertical: Location.end
                                        }}
                                    />
                                )}
                            />
                        </div>
                    </>
                )}
                {this.state.showWikiSecurityLoadingSpinner && <LoadingSpinner />}
            </div>
        );
    }

    private _onWikiPickerExpand = (): void => {
        if (!this._wikiLoaderPromise) {
            this._loadingWiki.value = true;
            const projectId = this.props.wikiInfo.wiki.projectId;
            this._wikiLoaderPromise = this.trackPromise(getWikiClient(this.context.pageContext).getAllWikis(projectId)).promise;
            this._wikiLoaderPromise.then((allWikis: WikiV2[]) => {
                // pop the loading element
                this._wikiGroupedItemProvider.pop();
                allWikis.forEach(wiki => {
                    wikiGroupNames.forEach(element => {
                        this._wikiGroupedItemProvider.pushGroups({ name: element.name, id: element.groupId });
                    });
                    this._wikiGroupedItemProvider.push({
                        id: wiki.id,
                        text: wiki.name,
                        groupId: wiki.type.toString(),
                        iconProps: { iconName: "Dictionary" }
                    });
                });

                this._wikiSelection.select(this._wikiGroupedItemProvider.value.findIndex(item => item.id === this.props.wikiInfo.wiki.id));
                this._loadingWiki.value = false;
            });
        }
    };

    private _onWikiSelectionChange = (event: React.SyntheticEvent<HTMLElement>, selection: IListBoxItem<{}>): void => {
        if (selection.text) {
            const selectedItem = selection.text;
            const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
            settingsService.setEntries({ WikiName: selectedItem }, SettingsUserScope.Me, "Project", this.props.wikiInfo.wiki.projectId);
            _wikiPickerMenuItemClicked(this.context.pageContext, TelemetryConstants.WikiSwitched, this.props.wikiInfo.wiki.type);
            navigateToUrl(this.context.pageContext, this.props.wikiInfo.wiki.projectId, selectedItem);
        }
    };

    private _onWikiVersionSelectionChange = (event: React.SyntheticEvent<HTMLElement>, selection: IListBoxItem<{}>): void => {
        _wikiVersionPickerMenuItemClicked(this.context.pageContext, TelemetryConstants.WikiVersionSwitched);
        navigateToUrl(this.context.pageContext, this.props.wikiInfo.wiki.projectId, this.props.wikiInfo.wiki.name, {
            wikiVersion: getVersionFromBranchName(selection.id)
        });
    };

    private _onRenderSelectedVersion = (selection: IListSelection, items: IListBoxItem<{}>[]): JSX.Element | string => {
        return (
            <div className="text-ellipsis">
                <Icon iconName="OpenSource" className="selected-wiki-version" />
                {items[selection.value[0].beginIndex].text || ""}
            </div>
        );
    };

    private _getMenuItems = (): IMenuItem[] => {
        const allWikiMenuItems: IMenuItem[] = [];
        const isWikiCodeWiki = isCodeWiki(this.props.wikiInfo.wiki);

        // Clone and wiki security are shown only for Project wikis
        if (!isWikiCodeWiki) {
            allWikiMenuItems.push({
                id: "cloneWiki",
                text: Resources.CloneWikiText,
                iconProps: { iconName: "CloneToDesktop" },
                onActivate: this._showCloneDialog
            });

            allWikiMenuItems.push({
                id: "wikiSecurity",
                text: Resources.WikiSecurityText,
                iconProps: { iconName: "Shield" },
                onActivate: this._showWikiSecurity,
                disabled: !(
                    this.props.wikiInfo.wikiPermissions.hasWikiContributePermission || this.props.wikiInfo.wikiPermissions.hasWikiManagePermission
                )
            });
        }

        allWikiMenuItems.push({
            id: "renameWiki",
            text: Resources.RenameWikiMenuText,
            iconProps: { iconName: "Rename" },
            onActivate: this._showRenameWikiDialog,
            disabled: !this.props.wikiInfo.wikiPermissions.hasWikiRenamePermission
        });

        // currently un publish is supported only for code wikis
        if (isWikiCodeWiki) {
            allWikiMenuItems.push({
                id: "unpublishWiki",
                text: Resources.UnpublishWikiText,
                onActivate: this._showUnpublishWikiDialog,
                disabled: !this.props.wikiInfo.wikiPermissions.hasWikiContributePermission,
                iconProps: { iconName: "DictionaryRemove" }
            });
        }

        return allWikiMenuItems;
    };

    private _showCloneDialog = (): void => {
        _showWrappedDialog(
            this.context.pageContext,
            TelemetryConstants.CloneWikiButtonClicked,
            {
                wikiRepositoryData: this.props.wikiInfo.wikiRepositoryData
            },
            "Wiki.CloneWikiDialog",
            ["ms.vss-wiki-web.wiki-clone-dialog"],
            this.props.wikiInfo.wiki.type
        );
    };

    private _showRenameWikiDialog = (): void => {
        _showWrappedDialog(
            this.context.pageContext,
            TelemetryConstants.RenameWikiButtonClicked,
            {
                wiki: this.props.wikiInfo.wiki
            },
            "Wiki.RenameWikiDialog",
            ["ms.vss-wiki-web.wiki-rename-dialog"],
            this.props.wikiInfo.wiki.type
        );
    };

    private _showPublishWikiPanel = (): void => {
        _showWrappedDialog(
            this.context.pageContext,
            TelemetryConstants.PublishCodeWikiButtonClicked,
            {},
            "Wiki.PublishCodeWikiPanel",
            ["ms.vss-wiki-web.publish-codewiki-panel-content"],
            this.props.wikiInfo.wiki.type
        );
    };

    private _showUnpublishWikiDialog = (): void => {
        _showWrappedDialog(
            this.context.pageContext,
            TelemetryConstants.UnpublishWikiButtonClicked,
            {
                wiki: this.props.wikiInfo.wiki,
                repositoryName: this.props.wikiInfo.wikiRepositoryData.repository.name
            },
            "Wiki.UnpublishWikiDialog",
            ["ms.vss-wiki-web.wiki-unpublish-dialog"],
            this.props.wikiInfo.wiki.type
        );
    };

    private _showWikiSecurity = (): void => {
        _wikiPickerMenuItemClicked(this.context.pageContext, TelemetryConstants.SecurityDialogPrompted, this.props.wikiInfo.wiki.type);
        const projectGuid = this.props.wikiInfo.wiki.projectId;
        const repoId = this.props.wikiInfo.wiki.repositoryId;

        this.setState({ showWikiSecurityLoadingSpinner: true });

        const legacyPlatformService = this.context.pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");
        this.trackPromise(legacyPlatformService.requireModules(["Admin/Scripts/TFS.Admin.Security"])).promise.then(([tfsAdminSecurity]) => {
            this.setState({ showWikiSecurityLoadingSpinner: false });

            const branchesSecurityManager = new tfsAdminSecurity.SecurityManager.create(VC_Constants.GitConstants.GitSecurityNamespaceId, {
                projectGuid: projectGuid
            });
            const branchSecurityToken = calculateGitSecuredToken(projectGuid, repoId);

            branchesSecurityManager.showPermissions(branchSecurityToken, Resources.WikiSecurityTitle, Resources.WikiSecurityTitle, undefined, "900");
        });
    };

    private _getWikiPickerActions = (): IButtonProps[] => {
        const actions: IButtonProps[] = [];
        if (this.props.showCreateNewProjectWikiAction) {
            actions.push({
                className: "wiki-picker-action",
                text: Resources.WikiCreateNewProjectWikiActionText,
                iconProps: { iconName: "Add" },
                onClick: this._onCreateWikiClick
            });
        }

        if (this.props.showPublishCodeWikiAction) {
            actions.push({
                className: "wiki-picker-action",
                text: Resources.WikiPublishWikiActionText,
                disabled: !isFeatureEnabled(this.context.pageContext, RepoServiceContributionId),
                iconProps: { iconName: "PublishContent" },
                onClick: this._showPublishWikiPanel
            });
        }
        return actions;
    };

    private _getWikiVersionPickerActions = (): IButtonProps[] => {
        const actions: IButtonProps[] = [];
        if (this.props.wikiInfo.wikiPermissions.hasWikiContributePermission) {
            actions.push({
                className: "wiki-picker-action",
                text: Resources.WikiPublishWikiVersionActionText,
                iconProps: { iconName: "PublishContent" },
                onClick: () =>
                    navigateToUrl(this.context.pageContext, this.props.wikiInfo.wiki.projectId, this.props.wikiInfo.wiki.name, {
                        [UrlConstants.ActionParam]: ActionIds.Update
                    })
            });
        }
        return actions;
    };

    private _onCreateWikiClick = (): void => {
        _wikiPickerMenuItemClicked(this.context.pageContext, TelemetryConstants.CreateProjectWikiButtonClicked, this.props.wikiInfo.wiki.type);
        this.trackPromise(
            getWikiClient(this.context.pageContext).createWiki({
                type: WikiType.ProjectWiki,
                projectId: this.props.wikiInfo.wiki.projectId
            } as WikiCreateParametersV2)
        ).promise.then((wiki: WikiV2) => {
            // change URL param if needed
            navigateToUrl(this.context.pageContext, wiki.projectId, wiki.name, {
                [UrlConstants.WikiVersionParam]: getVersionFromBranchName(wiki.versions[0].version)
            });
        });
    };
}

function _showWrappedDialog(
    pageContext: IVssPageContext,
    featureName: string,
    props: { [propertyName: string]: any },
    type: string,
    dependencies: string[],
    wikiType: WikiType
): void {
    _wikiPickerMenuItemClicked(pageContext, featureName, wikiType);
    showWrappedDialog(pageContext, props, type, dependencies);
}

function _wikiPickerMenuItemClicked(pageContext: IVssPageContext, featureName: string, wikiType: WikiType): void {
    publish(pageContext, featureName, wikiType, { source: TelemetryConstants.WikiPicker });
}

function _wikiVersionPickerMenuItemClicked(pageContext: IVssPageContext, featureName: string): void {
    publish(pageContext, featureName, undefined, { source: TelemetryConstants.WikiVersionPicker });
}

VssComponent.register("wiki-picker", WikiPicker);
